import { AssignStatus } from "@/enum/list.enum";

import { ListPerson } from "../../../List/schema/list";
import {
	getOutboxStatusChip,
	resolveOutboxBucket,
	resolveStepFailure,
	StatusChipDescriptor,
	StepFailure,
} from "./outbox-card-status.helper";

/** Which Messages surface a lead's status places it on — or none, for a lead outside outreach. */
export type ContactSurface = "inbox" | "outbox" | "review" | "archived" | null;

/**
 * Every assign status, placed. A `Record` over the enum rather than a lookup with a default, so a
 * new status fails the type check until somebody decides what the card says about it. The Outbox
 * and Review-to-send groups match `LIST_SCOPE_RELEVANCE_STATUSES`; archived leads sit in Review to
 * send but are not waiting for a review, so they read "Archived".
 */
export const CONTACT_SURFACE_BY_STATUS: Record<AssignStatus, ContactSurface> = {
	[AssignStatus.REPLIES]: "inbox",
	[AssignStatus.ASSIGNED]: "outbox",
	[AssignStatus.RUNNING]: "outbox",
	[AssignStatus.PAUSED]: "outbox",
	[AssignStatus.MESSAGE_SENT]: "outbox",
	[AssignStatus.ENDED]: "outbox",
	[AssignStatus.DONE]: "outbox",
	[AssignStatus.DRAFT]: "review",
	[AssignStatus.SAVED_FOR_LATER]: "review",
	[AssignStatus.ARCHIVED]: "archived",
	[AssignStatus.ASSIGNING]: null,
	[AssignStatus.BLACKLIST]: null,
	[AssignStatus.NOT_NOW]: null,
	[AssignStatus.NO_REPLY]: null,
	[AssignStatus.TO_BE_NURTURED]: null,
	[AssignStatus.DO_NOT_CONTACT]: null,
	[AssignStatus.FOLLOW_UP]: null,
	[AssignStatus.UNDER_REVIEW]: null,
	[AssignStatus.TRACKED_PROFILE]: null,
};

/** What the status row of a contact card shows: a failed step, a chip, or nothing. */
export type ContactStatus =
	| { kind: "failure"; failure: StepFailure }
	| { kind: "chip"; chip: StatusChipDescriptor }
	| null;

/**
 * The status a contact card shows in Find a person (PREC-4175). A lead in outreach reads exactly
 * like its Outbox card — the same bucket, chip and failure tag — so the two surfaces cannot
 * disagree about one lead; the other surfaces say where the lead is waiting.
 */
export const resolveContactStatus = (row: ListPerson, todayStart: Date): ContactStatus => {
	switch (CONTACT_SURFACE_BY_STATUS[row.assignStatus as AssignStatus] ?? null) {
		case "inbox":
			return { kind: "chip", chip: { labelKey: "outbox.statusChips.replied", tone: "success" } };
		case "review":
			return { kind: "chip", chip: { labelKey: "outbox.statusChips.toReview", tone: "muted" } };
		case "archived":
			return { kind: "chip", chip: { labelKey: "outbox.statusChips.archived", tone: "muted" } };
		case "outbox": {
			const failure = resolveStepFailure(row);

			if (failure) {
				return { kind: "failure", failure };
			}

			const chip = getOutboxStatusChip(resolveOutboxBucket(row, todayStart));

			return chip ? { kind: "chip", chip } : null;
		}

		default:
			return null;
	}
};
